import { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useProcurement } from '@/context/ProcurementContext';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, 
  Search, 
  Calendar,
  User,
  Phone,
  MapPin,
  Clock,
  CheckCircle,
  Filter
} from 'lucide-react';

const HandoverLog = () => {
  const { state, actions } = useProcurement();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    actions.fetchHandoverLogs();
    actions.fetchInvoices();
  }, []);

  // Filter handover logs
  const filteredLogs = state.handoverLogs.filter(log => {
    const matchesSearch = 
      log.handoverId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.floorManagerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.storageLocation.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || log.status === statusFilter;
    
    const matchesDate = (() => {
      if (dateFilter === 'all') return true;
      const logDate = new Date(log.handoverDateTime);
      const today = new Date();
      
      switch (dateFilter) {
        case 'today':
          return logDate.toDateString() === today.toDateString();
        case 'week':
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          return logDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          return logDate >= monthAgo;
        default:
          return true;
      }
    })();
    
    return matchesSearch && matchesStatus && matchesDate;
  }).sort((a, b) => new Date(b.handoverDateTime).getTime() - new Date(a.handoverDateTime).getTime());

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Handed Over':
        return 'default';
      case 'Completed':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getInvoiceDetails = (invoiceId: number) => {
    return state.invoices.find(invoice => invoice.id === invoiceId);
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Handover Log</h1>
            <p className="text-muted-foreground mt-2">
              View all completed invoice handovers to floor managers
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              {filteredLogs.length} Handovers
            </Badge>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Handovers</CardTitle>
              <FileText className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{state.handoverLogs.length}</div>
              <p className="text-xs text-muted-foreground">
                All completed handovers
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Today</CardTitle>
              <Calendar className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {state.handoverLogs.filter(log => {
                  const today = new Date().toDateString();
                  const logDate = new Date(log.handoverDateTime).toDateString();
                  return logDate === today;
                }).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Handovers today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Week</CardTitle>
              <Clock className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {state.handoverLogs.filter(log => {
                  const logDate = new Date(log.handoverDateTime);
                  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                  return logDate >= weekAgo;
                }).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Handovers this week
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <CheckCircle className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {state.handoverLogs.filter(log => log.status === 'Completed').length}
              </div>
              <p className="text-xs text-muted-foreground">
                Fully completed handovers
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search by handover ID, invoice, manager, or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Handed Over">Handed Over</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Handover Log Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Handover History</CardTitle>
            <CardDescription>
              Complete log of all invoice handovers to floor managers
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredLogs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                    <FileText className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">No Handover Records</h3>
                    <p className="text-sm">
                      Handover records will appear here once invoices are handed over
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Handover ID</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Floor Manager</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Invoice Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => {
                    const invoice = getInvoiceDetails(log.invoiceId);
                    const vendor = invoice ? state.vendors.find(v => v.id === invoice.vendorId) : null;
                    
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">{log.handoverId}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{log.invoiceNumber}</span>
                            {invoice && (
                              <span className="text-xs text-muted-foreground">
                                {formatCurrency(invoice.totalAmount)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {log.floorManagerName}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            {log.floorManagerContact}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div className="flex flex-col">
                              <span className="text-sm">
                                {new Date(log.handoverDateTime).toLocaleDateString()}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(log.handoverDateTime).toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            {log.storageLocation}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusVariant(log.status)}>
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {invoice ? (
                            <div className="flex flex-col gap-1">
                              <div className="text-sm">
                                <span className="font-medium">Vendor:</span> {vendor?.name || invoice.vendorName}
                              </div>
                              <div className="text-sm">
                                <span className="font-medium">Items:</span> {invoice.items.length} items
                              </div>
                              <div className="text-sm">
                                <span className="font-medium">PO:</span> {invoice.poNumber}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">Invoice not found</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default HandoverLog;
